import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils';

interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  label?: string;
  error?: string;
  helperText?: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /**
   * Fires on every keystroke in the search box with the raw typed text.
   * Optional — lets a caller drive a server-side search (e.g. RTO lookup)
   * that repopulates `options`, on top of the built-in client-side filter
   * that still runs against whatever `options` currently holds.
   */
  onQueryChange?: (query: string) => void;
}

/**
 * A searchable single-select dropdown. Controlled via `value`/`onChange` so it
 * plugs into react-hook-form through `watch` + `setValue`. Styling mirrors the
 * native `Select` so the two are visually interchangeable.
 */
export function SearchableSelect({
  label,
  error,
  helperText,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  required,
  disabled,
  id,
  onQueryChange,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        onQueryChange?.('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onQueryChange]);

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
          className={cn(
            'w-full pl-3 pr-1.5 py-2 border rounded-lg transition-colors duration-200 text-left flex items-center justify-between',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent',
            'bg-white dark:bg-gray-700 cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800',
            error
              ? 'border-red-300 focus:ring-red-500 dark:border-red-700 dark:focus:ring-red-400'
              : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
          )}
        >
          <span className={cn('truncate', selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronUpDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-72 overflow-hidden">
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  onQueryChange?.(e.target.value);
                }}
                placeholder="Search..."
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
              />
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
              )}
              {filtered.map((o) => (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                      setQuery('');
                      onQueryChange?.('');
                    }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-primary-50 dark:hover:bg-primary-900/30',
                      o.value === value ? 'text-primary-700 font-medium dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    <span className="truncate">{o.label}</span>
                    {o.value === value && <CheckIcon className="w-4 h-4 flex-shrink-0" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {helperText && !error && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>}
    </div>
  );
}
